/**
 * Platform Ad Analytics Page - Comprehensive ad performance dashboard
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  AdAnalyticsSummary,
  PlacementPerformanceCard,
  PlatformMonetizationMetrics
} from '@/components/analytics';
import {
  BarChart3,
  Calendar,
  RefreshCw,
  Download,
  Eye,
  MousePointer,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdAnalytics() {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [period, setPeriod] = useState(30);
  
  const fetchAnalytics = async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      // Fetch platform summary
      const summaryRes = await fetch(`${API_URL}/api/ad-tracking/analytics/summary?days=${period}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      
      // Fetch placements performance
      const placementsRes = await fetch(`${API_URL}/api/ad-tracking/analytics/placements?days=${period}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (placementsRes.ok) {
        const data = await placementsRes.json();
        setPlacements(data.placements || []);
      }
      
      // Fetch daily stats
      const dailyRes = await fetch(`${API_URL}/api/ad-tracking/analytics/daily?days=${period}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (dailyRes.ok) {
        const data = await dailyRes.json();
        setDailyStats(data.daily_stats || []);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchAnalytics();
  }, [session?.access_token, period]);
  
  // Simple bar chart for daily stats
  const maxImpressions = Math.max(...dailyStats.map(d => d.impressions), 1);
  
  return (
    <div className="p-6 space-y-6" data-testid="ad-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Ad Analytics</h1>
          <p className="text-white/50 text-sm mt-1">Platform-wide advertising performance</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg p-1">
            {[7, 30, 90].map(days => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  period === days 
                    ? 'bg-white/10 text-white' 
                    : 'text-white/50 hover:text-white'
                }`}
              >
                {days}d
              </button>
            ))}
          </div>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={fetchAnalytics}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && !summary ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-white/20 animate-spin" />
        </div>
      ) : (
        <>
          {/* Platform Summary */}
          <PlatformMonetizationMetrics data={summary} />
          
          {/* Key Metrics */}
          <AdAnalyticsSummary data={summary} />
          
          {/* Daily Trend Chart */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Daily Performance</h3>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                  Impressions
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                  Clicks
                </span>
              </div>
            </div>
            
            {/* Simple bar chart */}
            <div className="h-48 flex items-end gap-1">
              {dailyStats.slice(-30).map((day, i) => (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col items-center gap-0.5">
                    <div 
                      className="w-full bg-purple-500/30 rounded-t"
                      style={{ height: `${(day.impressions / maxImpressions) * 150}px` }}
                      title={`${day.impressions} impressions`}
                    />
                    {day.clicks > 0 && (
                      <div 
                        className="w-full bg-emerald-500/50 rounded"
                        style={{ height: `${Math.max((day.clicks / maxImpressions) * 150, 2)}px` }}
                        title={`${day.clicks} clicks`}
                      />
                    )}
                  </div>
                  {i % 5 === 0 && (
                    <span className="text-[10px] text-white/30 mt-1">{day.date.slice(5)}</span>
                  )}
                </div>
              ))}
            </div>
            
            {dailyStats.length === 0 && (
              <div className="h-48 flex items-center justify-center">
                <p className="text-white/40">No data for selected period</p>
              </div>
            )}
          </div>
          
          {/* Placement Performance */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Placement Performance</h3>
            {placements.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {placements.map(placement => (
                  <PlacementPerformanceCard
                    key={placement.placement_key}
                    placement={{ placement_key: placement.placement_key, placement_name: placement.placement_key }}
                    performance={placement}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-8 text-center">
                <BarChart3 className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No placement data yet</p>
                <p className="text-white/30 text-sm mt-1">Impressions will appear here once ads are served</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
